import { useState, type FC } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import Dialog from '@/storybook/components/Dialog/Dialog';
import Text from '@/storybook/components/Text/Text';
import { SharedViewScenario, MissingFilterEntry } from '@/hooks/useSharedViewValidation';
import CircleInfoIcon from '@/storybook/icons/CircleInfoIcon';
import SharedViewErrorActions from './SharedViewErrorActions/SharedViewErrorActions';
import SharedViewErrorDetails from './SharedViewErrorDetails/SharedViewErrorDetails';
import type { SharedViewFailureDetails } from '@/utils/filter/sharedViewDiagnostics';
import styles from './SharedViewDialog.module.css';

export type SharedViewSource = 'sharedLink' | 'savedFilter';

export interface SharedViewDialogProps {
  isOpen: boolean;
  scenario: SharedViewScenario;
  missingFilters: MissingFilterEntry[];
  senderCountryLabel?: string;
  recipientCountryLabel?: string;
  source?: SharedViewSource;
  errorDetails?: SharedViewFailureDetails;
  isDetailsEnabled?: boolean;
  onApply: () => void;
  onCancel: () => void;
}

/** Format missing filters as inline comma-separated string: 'Label: Value', 'Label: Value' */
const formatMissingFilters = (missingFilters: MissingFilterEntry[], t: (key: string) => string): string =>
  missingFilters
    .flatMap(filterEntry =>
      filterEntry.missingValues.map(filterValue => `'${t(filterEntry.filterLabel)}: ${filterValue}'`),
    )
    .join(', ');

/** Render text with bold {{placeholders}} */
const renderWithBoldPlaceholders = (template: string, replacements: Record<string, string>): React.ReactNode[] => {
  const keys = Object.keys(replacements).map(objectKey => `{{${objectKey}}}`);
  const pattern = new RegExp(`(${keys.join('|')})`, 'g');
  const parts = template.split(pattern);
  return parts.map((part, iterator) => {
    const match = part.match(/^{{(.+)}}$/);
    if (match && replacements[match[1]]) return <strong key={iterator}>{replacements[match[1]]}</strong>;
    return part;
  });
};

interface SharedViewErrorBodyProps {
  errorDetails?: SharedViewFailureDetails;
  areDetailsVisible: boolean;
}

const SharedViewErrorBody: FC<SharedViewErrorBodyProps> = ({ errorDetails, areDetailsVisible }) => {
  const { t } = useTranslation();
  const systemError = t('global.filters.sharedViewDialog.systemError');
  const corruptedUrl = t('global.filters.sharedViewDialog.corruptedUrl');

  return (
    <>
      {errorDetails?.diagnosis.kind === 'recordNotFound' ? (
        <Text className={styles.bodyText} data-testid="shared-view-dialog-body">
          {t('global.filters.sharedViewDialog.errorBodyRecordNotFound')}
        </Text>
      ) : (
        <Text className={styles.bodyText} data-testid="shared-view-dialog-body">
          {renderWithBoldPlaceholders(
            t('global.filters.sharedViewDialog.errorBody', {
              systemError: '{{systemError}}',
              corruptedUrl: '{{corruptedUrl}}',
              interpolation: { escapeValue: false },
            }),
            { systemError, corruptedUrl },
          )}
        </Text>
      )}
      <Text className={styles.bodyText} data-testid="shared-view-dialog-error-detail">
        {t('global.filters.sharedViewDialog.errorBodyDetail')}
      </Text>
      {errorDetails && areDetailsVisible && <SharedViewErrorDetails details={errorDetails} />}
    </>
  );
};

const SharedViewDialog: FC<SharedViewDialogProps> = ({
  isOpen,
  scenario,
  missingFilters,
  senderCountryLabel,
  recipientCountryLabel,
  source = 'sharedLink',
  errorDetails,
  isDetailsEnabled = false,
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [areDetailsVisible, setAreDetailsVisible] = useState(false);

  const handleToggleDetails = () => setAreDetailsVisible(isVisible => !isVisible);

  const unknownCountry = t('global.filters.sharedViewDialog.unknownCountry');
  const sender = senderCountryLabel ?? unknownCountry;
  const recipient = recipientCountryLabel ?? unknownCountry;

  const getTitle = () => {
    let label: string;
    if (scenario === SharedViewScenario.ERROR) {
      label = t('global.filters.sharedViewDialog.errorTitle');
    } else if (scenario === SharedViewScenario.VALUES_NOT_FOUND) {
      label = t('global.filters.sharedViewDialog.valuesNotFoundTitle');
    } else {
      label = t('global.filters.sharedViewDialog.updateRegionTitle');
    }

    return (
      <>
        <CircleInfoIcon className={styles.titleIcon} />
        {label}
      </>
    );
  };

  const renderContent = () => {
    switch (scenario) {
      case SharedViewScenario.REGION_MISMATCH:
        return (
          <Text className={styles.bodyText} data-testid="shared-view-dialog-body">
            {renderWithBoldPlaceholders(
              t('global.filters.sharedViewDialog.regionMismatchBody', {
                senderCountry: '{{senderCountry}}',
                recipientCountry: '{{recipientCountry}}',
                interpolation: { escapeValue: false },
              }),
              { senderCountry: sender, recipientCountry: recipient },
            )}
          </Text>
        );

      case SharedViewScenario.REGION_MISMATCH_WITH_MISSING: {
        const filterList = formatMissingFilters(missingFilters, t);
        return (
          <>
            <Text className={styles.bodyText} data-testid="shared-view-dialog-body">
              {renderWithBoldPlaceholders(
                t('global.filters.sharedViewDialog.regionMismatchWithMissingIntro', {
                  senderCountry: '{{senderCountry}}',
                  recipientCountry: '{{recipientCountry}}',
                  interpolation: { escapeValue: false },
                }),
                { senderCountry: sender, recipientCountry: recipient },
              )}
            </Text>
            <Text className={styles.bodyText} data-testid="shared-view-dialog-filter-list">
              {renderWithBoldPlaceholders(
                t('global.filters.sharedViewDialog.regionMismatchWithMissingDetail', {
                  filterList: '{{filterList}}',
                  interpolation: { escapeValue: false },
                }),
                { filterList },
              )}
            </Text>
          </>
        );
      }

      case SharedViewScenario.VALUES_NOT_FOUND: {
        const filterList = formatMissingFilters(missingFilters, t);
        if (source === 'savedFilter') {
          return (
            <>
              <Text className={styles.bodyText} data-testid="shared-view-dialog-filter-list">
                {renderWithBoldPlaceholders(
                  t('global.filters.sharedViewDialog.valuesNotFoundBodySavedFilter', {
                    filterList: '{{filterList}}',
                    interpolation: { escapeValue: false },
                  }),
                  { filterList },
                )}
              </Text>
              <Text className={styles.bodyText} data-testid="shared-view-dialog-apply-hint">
                {t('global.filters.sharedViewDialog.valuesNotFoundApplyHint')}
              </Text>
            </>
          );
        }
        return (
          <Text className={styles.bodyText} data-testid="shared-view-dialog-filter-list">
            {renderWithBoldPlaceholders(
              t('global.filters.sharedViewDialog.valuesNotFoundBody', {
                filterList: '{{filterList}}',
                interpolation: { escapeValue: false },
              }),
              { filterList },
            )}
          </Text>
        );
      }

      case SharedViewScenario.ERROR:
        return (
          <SharedViewErrorBody errorDetails={errorDetails} areDetailsVisible={isDetailsEnabled && areDetailsVisible} />
        );

      default:
        return null;
    }
  };

  const getApplyButtonLabel = () => {
    switch (scenario) {
      case SharedViewScenario.REGION_MISMATCH:
        return t('global.filters.sharedViewDialog.viewDataForRegion', {
          recipientCountry: recipient,
        });
      case SharedViewScenario.REGION_MISMATCH_WITH_MISSING:
        return t('global.filters.sharedViewDialog.updateAndViewData');
      default:
        return t('global.filters.sharedViewDialog.applyAvailableFilters');
    }
  };

  const renderFooter = () => {
    if (scenario === SharedViewScenario.ERROR) {
      return (
        <SharedViewErrorActions
          errorDetails={isDetailsEnabled ? errorDetails : undefined}
          areDetailsVisible={areDetailsVisible}
          onToggleDetails={handleToggleDetails}
          onApply={onApply}
        />
      );
    }

    return (
      <>
        <Button variant="outlined" onClick={onCancel} data-testid="shared-view-dialog-cancel">
          {t('global.filters.sharedViewDialog.cancel')}
        </Button>
        <Button variant="primary" onClick={onApply} data-testid="shared-view-dialog-apply">
          {getApplyButtonLabel()}
        </Button>
      </>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={getTitle()}
      size="S"
      footer={renderFooter()}
      className={cx(styles.sharedViewDialog, {
        [styles.errorDialog]: scenario === SharedViewScenario.ERROR && isDetailsEnabled && areDetailsVisible,
      })}
      data-testid="shared-view-dialog"
    >
      {renderContent()}
    </Dialog>
  );
};

export default SharedViewDialog;
